import React, { useCallback, useMemo } from 'react';

import { Icons, NumberInput, Tooltip } from '@components/common';

import { IThreshold } from '@interfaces/alarms/threshold';
import { IChangeThreshold } from '../interfaces';

import { EThresholdTypes } from '../enums';

import {
	DEFAULT_PLACEHOLDER,
	ERROR_MESSAGE,
	ERROR_MESSAGE_MAX,
	ERROR_MESSAGE_MIN,
	MAX_PERCENT,
} from '../../constants';

import styles from './ThresholdValue.module.scss';

interface IThresholdValueProps {
	threshold: IThreshold;
	onChange: (value: IChangeThreshold) => void;
	isDisabled: boolean;
	setIsError?: (isError: boolean) => void;
}

const ThresholdValue = ({
	threshold,
	onChange,
	isDisabled,
}: IThresholdValueProps) => {
	const isPassThresholdRange = useMemo(() => {
		return (threshold.minThreshold === null) || (threshold.maxThreshold === null)
			|| (threshold.minThreshold < threshold.maxThreshold);
	}, [threshold.maxThreshold, threshold.minThreshold]);

	const createError = useCallback((thresholdValue: number | null, errorMessage: string) => {
		return (thresholdValue || 0) > MAX_PERCENT
			? ERROR_MESSAGE
			: !isPassThresholdRange
				? errorMessage
				: undefined;
	}, [isPassThresholdRange]);

	const errorMin = useMemo(() => {
		return createError(threshold?.minThreshold, ERROR_MESSAGE_MIN);
	}, [threshold.minThreshold, createError]);

	const errorMax = useMemo(() => {
		return createError(threshold?.maxThreshold, ERROR_MESSAGE_MAX);
	}, [threshold.maxThreshold, createError]);

	const onChangeMin = (value: string) => {
		onChange({
			value: value.length ? +value : null,
			kpiId: threshold.kpiId,
			type: EThresholdTypes.min,
		});
	};

	const onChangeMax = (value: string) => {
		onChange({
			value: value.length ? +value : null,
			kpiId: threshold.kpiId,
			type: EThresholdTypes.max,
		});
	};

	return (
		<div className={styles.thresholdValueContainer}>
			<div className={styles.title}>
				{threshold.kpiName}
				{!!threshold.tooltip?.length && (
					<>
						&nbsp;
						<Tooltip content={threshold.tooltip}>{Icons.info()}</Tooltip>
					</>
				)}
			</div>
			<div>
				<NumberInput
					disabled={isDisabled}
					errorMessage={errorMin}
					maxValue={MAX_PERCENT}
					name={`min-${threshold.kpiName}`}
					onChange={onChangeMin}
					placeholder={DEFAULT_PLACEHOLDER}
					value={threshold.minThreshold?.toString()}
				/>
			</div>
			<div>
				<NumberInput
					disabled={isDisabled}
					errorMessage={errorMax}
					maxValue={MAX_PERCENT}
					name={`max-${threshold.kpiName}`}
					onChange={onChangeMax}
					placeholder={DEFAULT_PLACEHOLDER}
					value={threshold.maxThreshold?.toString()}
				/>
			</div>
		</div>
	);
};

export default ThresholdValue;
